import React, { useEffect, useState } from 'react';
import { Box, Button, Group, TextInput, Grid, Col } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import ApiLinks from '../../Navlinks/ApiLinks';
import ImageUploadWithPreview from '../../Utilities/ImageUploadWithPreview';
import LoaderOverlay from '../../Utilities/LoadingOverLay';
import CustomMantineTable from '../../Utilities/CustomMantineTable';
import { DeleteRecords, EditRecord } from '../../Utilities/EditorDeleteRecords';



export default function Floorplan({ id }) {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);



  const form = useForm({
    initialValues: {
      amount: '',
      configurationname: '',
      configurationimage: null,
      builtuparea: '',
      configurationdesc: '',
      basesellingprice: '',
      bookingamt: '',
      pricesqft: '',
      propertyid: id || '',
      id: ''
    },
    validate: {

      amount: (val) => (val ? null : 'Required'),
      configurationname: (val) => (val ? null : 'Required'),
      builtuparea: (val) => (val ? null : 'Required'),
      configurationdesc: (val) => (val ? null : 'Required'),
      basesellingprice: (val) => (val ? null : 'Required'),
      bookingamt: (val) => (val ? null : 'Required'),
      pricesqft: (val) => (val ? null : 'Required')
    }
  });


  useEffect(() => {
    if (id) {
      setLoading(true);
      // loadData also 
      loadData(id);
    }

  }, [id, user.compcode]);


  // Loading FloorList
  const loadData = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/${ApiLinks.FloorPlan_GetById}/${id}/${user.compcode}`
      );
      if (response.data.status) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (values) => {

    if (!values.propertyid) {
      toast.error('Save property overview first...');
      return; // stop form submission if property ID is missing
    }


    if (!selectedFile && !existingImagePath) {
      toast.error('Please upload Configuration Image');
      return; // stop form submission if no file or existing image
    }

   

    const formData = new FormData();
    formData.append('amount', values.amount.toString());
    formData.append('configurationname', values.configurationname.toString());
    formData.append('builtuparea', values.builtuparea.toString());
    formData.append('configurationdesc', values.configurationdesc);
    formData.append('basesellingprice', values.basesellingprice.toString());
    formData.append('bookingamt', values.bookingamt.toString());
    formData.append('pricesqft', values.pricesqft.toString());
    formData.append('propertyid', values.propertyid.toString());
    formData.append('id', 0);
    formData.append('compcode', user.compcode);
    if (selectedFile) {
      formData.append('configurationimage', selectedFile);
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/${ApiLinks.FloorPlan_Save}`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.status) {
        toast.success('Floor plan saved successfully');

        form.reset(); // Reset all fields to initialValues
        form.setFieldValue('propertyid', values.propertyid.toString()); // Set propertyid back
        setSelectedFile(null); // Clear file

        loadData(id);

      } else {
        toast.error(res.data.message || 'Failed to save');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error submitting form');
    }
  };

  //
  const columns = [
    {
      id: 'actions',
      header: 'Actions',
      size: '100',
      Cell: ({ row }) => (
        <Group spacing="xs">
          <DeleteRecords
            id={row.original.id}
            endpoint={`${ApiLinks.FloorPlan_Delete}`}
            compcode={user.compcode}
            onDeleteSuccess={(deletedId) =>
              setData((prev) => prev.filter((item) => item.id !== deletedId))
            }
          />
        </Group>
      ),
    },
    {
      accessorKey: 'configurationname',
      header: 'Configuration Nname',
    },
    {
      accessorKey: 'configurationdesc',
      header: 'Configuration Desc',
    },
    {
      accessorKey: 'builtuparea',
      header: 'Builtup Area',
    },
    {
      accessorKey: 'basesellingprice',
      header: 'Base Selling Price',
    },
    {
      accessorKey: 'pricesqft',
      header: 'Price Sqft',
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
    },
    {
      accessorKey: 'bookingamt',
      header: 'Book Amount',
    },
    {
      accessorKey: 'configurationimage',
      header: 'Photo',
      Cell: ({ cell }) => {
        const photoPath = cell.getValue();
        const imageUrl = `${process.env.REACT_APP_IMG_URL}/${photoPath}`;
        return (
          <a href={imageUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={imageUrl}
              alt="configurationimage"
              style={{ width: 'auto', height: '50px', objectFit: 'cover' }}
            />
          </a>
        );
      },
    },



  ];



  if (loading) return <LoaderOverlay />;

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Grid gutter="md">
          {/* Left side: col-8 */}
          <Col span={12} md={10}>
            <Grid gutter="md">
              <Col span={12} md={6}>
                <TextInput
                  label="Configuration Name *"
                  placeholder="e.g. 3BHK"
                  {...form.getInputProps('configurationname')}
                />
              </Col>
              <Col span={12} md={6}>
                <TextInput
                  label="Configuration Description *"
                  placeholder="e.g. 3BHK in The Prestige City"
                  {...form.getInputProps('configurationdesc')}
                />
              </Col>
              <Col span={6} md={2}>
                <TextInput
                  label="Built-up Area *"
                  placeholder="Sq. ft"
                  {...form.getInputProps('builtuparea')}
                />
              </Col>
              <Col span={6} md={2}>
                <TextInput
                  label="Base Selling Price *"
                  placeholder="e.g. 5,000,000"
                  {...form.getInputProps('basesellingprice')}
                />
              </Col>
              <Col span={6} md={2}>
                <TextInput
                  label="Booking Amount *"
                  placeholder="e.g. 10%"
                  {...form.getInputProps('bookingamt')}
                />
              </Col>
              <Col span={6} md={3}>
                <TextInput
                  label="Amount *"
                  placeholder="e.g. 150000"
                  {...form.getInputProps('amount')}
                />
              </Col>
              <Col span={12} md={3}>
                <TextInput
                  label="Price per Sqft *"
                  placeholder="e.g. 10,000"
                  {...form.getInputProps('pricesqft')}
                />
              </Col>
            </Grid>
          </Col>

          {/* Right side: col-4 */}
          <Col span={12} md={2} >
            <Group position="right">
              <ImageUploadWithPreview
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                existingImagePath={existingImagePath}
                setExistingImagePath={setExistingImagePath}
                label="Upload Floor Plan Image 16:9 "
                fullWidthPreview={true}
              />
            </Group>
          </Col>


          <Col span={12} >
            <Group position="right" mt="md">
              <Button type="submit">Save</Button>
              <Button variant="outline" onClick={() => navigate('/dashboard/floorplans')}>
                Cancel
              </Button>
            </Group>
          </Col>

          <Col span={12} mt="lg" >

            <CustomMantineTable columns={columns} data={data} loading={loading} />
          </Col>

        </Grid>

      </form>
    </Box>
  );
}
